import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { obterAlvo } from "./registro";

/**
 * Tour — camada visual: escurecimento com recorte, balão e navegação.
 *
 * Pegadinhas tratadas (todas do roteiro da feature):
 *  - UMA camada com buraco: o recorte é um div com box-shadow de 9999px —
 *    nada de 4 retângulos brigando. Passo sem alvo usa o MESMO mecanismo com
 *    recorte de tamanho zero no centro (vira escurecimento total).
 *  - PORTAL no body com z-index acima de tudo: ancestral com transform/filter
 *    criaria stacking context e prenderia o overlay atrás de botões flutuantes.
 *  - mede o alvo só depois de scrollIntoView + DOIS requestAnimationFrame, e
 *    remede em resize/rotação.
 *  - alvo mais alto que a viewport: recorta só a parte de cima
 *    (altura máx = innerHeight − 240), senão o balão ancora fora da tela.
 *  - balão abaixo do recorte se couber; senão ACIMA ancorado pelo PÉ (bottom),
 *    porque a altura do balão varia com o texto.
 *  - botão físico "voltar" fecha o TOUR, não sai da tela (estado no history +
 *    popstate). StrictMode roda efeitos 2×: refs guardam o push e o motivo do
 *    fechamento pra o cleanup não disparar um popstate fantasma.
 */
const Z = 2147483000;
const MARGEM = 10;   // respiro entre o alvo e a borda do recorte
const BORDA = 8;     // distância mínima de qualquer coisa até a borda da tela
const RESERVA = 230; // espaço que o balão precisa (altura típica + respiro)

export default function TourOverlay({ passos, onFechar, onConcluir }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const balaoRef = useRef(null);
  const empilhouRef = useRef(false);   // StrictMode: efeito roda 2×, empilha 1×
  const fechouPorPopRef = useRef(false);
  const passo = passos[idx];

  // ── medição do alvo do passo atual ──
  const medir = useCallback(() => {
    if (!passo || !passo.alvo) { setRect(null); return; }
    const el = obterAlvo(passo.alvo);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "instant" });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth, vh = window.innerHeight;

      // O recorte é CLAMPADO À TELA nos quatro lados — destaca a parte visível
      // do alvo. Sem isto: alvo com margem negativa (ex.: `-mx-10`) vaza pela
      // direita, e alvo mais alto que a viewport fica com topo negativo (o
      // scrollIntoView centraliza), levando o balão pra fora da tela.
      let topo = Math.max(BORDA, r.top - MARGEM);
      let esq = Math.max(BORDA, r.left - MARGEM);
      let dir = Math.min(vw - BORDA, r.right + MARGEM);
      let base = Math.min(vh - BORDA, r.bottom + MARGEM);

      // O balão precisa caber em ALGUM lado. Quando o alvo ocupa quase toda a
      // tela, encolhe o recorte pela base pra abrir espaço embaixo — melhor
      // destacar um pedaço do alvo do que perder o balão fora da tela.
      if (vh - base < RESERVA && topo < RESERVA) {
        base = Math.max(topo + 80, vh - RESERVA);
      }

      setRect({
        top: topo, left: esq,
        width: Math.max(0, dir - esq),
        height: Math.max(0, base - topo),
        raio: passo.raio ?? 12,
      });
    }));
  }, [passo]);

  useEffect(() => { medir(); }, [medir]);
  useEffect(() => {
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, [medir]);

  // ── trava a rolagem do body enquanto o tour roda ──
  useEffect(() => {
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = antes; };
  }, []);

  // ── fechamento SEMPRE passa pelo popstate ──
  // `history.back()` é ASSÍNCRONO: finalizar antes do pop assentar cria a
  // corrida clássica — o matarPreview() do decisor dava replaceState na
  // entrada ATUAL e o back() restaurava a anterior COM `?tour=1` de volta.
  // Então: fechar por UI só agenda (pendenteRef) e dispara back(); quem
  // finaliza é o handler de popstate, com a pilha já assentada. O botão
  // físico "voltar" (Android/WebView) cai no MESMO caminho de graça.
  const pendenteRef = useRef(null);      // null | {concluiu} agendado pela UI
  const finalizarRef = useRef(null);     // callbacks atuais sem re-registrar o listener
  finalizarRef.current = (concluiu) => (concluiu ? onConcluir() : onFechar());

  useEffect(() => {
    if (!empilhouRef.current) {          // StrictMode roda efeitos 2× — empilha 1×
      window.history.pushState({ tour: true }, "");
      empilhouRef.current = true;
    }
    const aoPop = () => {
      fechouPorPopRef.current = true;
      const pend = pendenteRef.current;
      pendenteRef.current = null;
      finalizarRef.current(pend ? pend.concluiu : false);
    };
    window.addEventListener("popstate", aoPop);
    return () => window.removeEventListener("popstate", aoPop);
  }, []);

  const fecharPorUI = useCallback((concluiu) => {
    if (empilhouRef.current && !fechouPorPopRef.current) {
      pendenteRef.current = { concluiu };
      window.history.back();             // o popstate finaliza
      return;
    }
    finalizarRef.current(concluiu);
  }, []);

  // ── foco no balão a cada troca de passo (acessibilidade) ──
  useEffect(() => { balaoRef.current?.focus(); }, [idx]);

  const ultimo = idx === passos.length - 1;
  const proximo = () => (ultimo ? fecharPorUI(true) : setIdx((i) => i + 1));

  // ── geometria do recorte e do balão ──
  // `rect` já vem clampado e com o respiro embutido — não somar MARGEM de novo.
  const foco = rect || { top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0, raio: 0 };
  const vh = window.innerHeight;
  const espacoAbaixo = rect ? vh - (foco.top + foco.height) : 0;
  const espacoAcima = rect ? foco.top : 0;
  // Abaixo se couber; senão acima (ancorado pelo PÉ, porque a altura do balão
  // varia com o texto); se nenhum couber, abaixo — o clamp da medição garantiu
  // o espaço. `maxHeight` evita que um texto longo estoure pelo outro lado.
  const posBalao = !rect
    ? { top: "50%", left: "50%", transform: "translate(-50%,-50%)" }
    : espacoAbaixo >= RESERVA || espacoAbaixo >= espacoAcima
      ? { top: Math.min(foco.top + foco.height + 12, vh - 90), left: "50%", transform: "translateX(-50%)",
          maxHeight: vh - (foco.top + foco.height) - 20 }
      : { bottom: Math.min(vh - foco.top + 12, vh - 90), left: "50%", transform: "translateX(-50%)",
          maxHeight: foco.top - 20 };

  const Botao = ({ children, onClick, primario }) => (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`min-h-[44px] min-w-[44px] px-4 rounded-xl text-sm font-semibold transition-colors ${
        primario ? "bg-[#FF6600] hover:bg-[#e55a00] text-white" : "text-white/70 hover:text-white"
      }`}
    >
      {children}
    </button>
  );

  return createPortal(
    // toque em QUALQUER lugar avança — o passo mostra ONDE o botão fica, não é
    // pra tocar nele agora.
    <div style={{ position: "fixed", inset: 0, zIndex: Z }} onClick={proximo}>
      <div
        style={{
          position: "fixed", top: foco.top, left: foco.left, width: foco.width, height: foco.height,
          borderRadius: foco.raio, boxShadow: "0 0 0 9999px rgba(0,0,0,.72)",
          pointerEvents: "none", transition: "all .25s ease", zIndex: Z,
        }}
      />
      <div
        ref={balaoRef}
        role="dialog"
        aria-modal="true"
        aria-label={passo.titulo}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{ position: "fixed", zIndex: Z + 1, maxWidth: "min(92vw, 380px)", width: "100%", ...posBalao }}
        className="bg-[#101826] text-white rounded-2xl border border-white/10 shadow-2xl p-4 outline-none overflow-y-auto"
      >
        <p className="text-[11px] font-bold text-[#FF8533] uppercase tracking-wider mb-1">
          {idx + 1} de {passos.length}
        </p>
        <h3 className="font-bold text-base leading-snug">{passo.titulo}</h3>
        <p className="text-sm text-white/80 mt-1.5 leading-relaxed">{passo.texto}</p>
        <div className="flex items-center justify-between mt-4 gap-2">
          <Botao onClick={() => fecharPorUI(false)}>Pular</Botao>
          <div className="flex gap-2">
            {idx > 0 && <Botao onClick={() => setIdx((i) => i - 1)}>Voltar</Botao>}
            <Botao primario onClick={proximo}>{ultimo ? "Entendi ✓" : "Próximo"}</Botao>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
